import { NoteConfigExtension } from '@blocksuite/affine-block-note';
import {
  calculateGridScreenGap,
  createEdgelessGridBackgroundImage,
  DefaultTool,
  EDGELESS_GRID_BACKGROUND_IMAGE_VAR,
  EDGELESS_GRID_STYLE_ATTRIBUTE,
  EDGELESS_GRID_STYLE_CHANGED_EVENT,
  type EdgelessGridStyle,
  normalizeEdgelessGridStyle,
  normalizeWheelDeltaY,
  type SurfaceBlockComponent,
  type SurfaceBlockModel,
} from '@blocksuite/affine-block-surface';
import { isSingleMindMapNode } from '@blocksuite/affine-gfx-mindmap';
import { PanTool } from '@blocksuite/affine-gfx-pointer';
import { mountShapeTextEditor } from '@blocksuite/affine-gfx-shape';
import { clearTextMeasurementCache } from '@blocksuite/affine-gfx-text';
import {
  NoteBlockModel,
  type RootBlockModel,
  type ShapeElementModel,
} from '@blocksuite/affine-model';
import {
  EditorSettingProvider,
  EditPropsStore,
  FontLoaderService,
  ThemeProvider,
  ViewportElementProvider,
} from '@blocksuite/affine-shared/services';
import {
  edgelessCanvasBackgroundVar,
  edgelessCanvasGridColorVar,
  edgelessCanvasLineGridColorVars,
  type EdgelessLineGridPalette,
} from '@blocksuite/affine-shared/styles';
import {
  EdgelessWheelGestureRouter,
  matchModels,
  requestConnectedFrame,
  requestThrottledConnectedFrame,
} from '@blocksuite/affine-shared/utils';
import { Bound, Point, Vec } from '@blocksuite/global/gfx';
import {
  BlockComponent,
  type GfxBlockComponent,
  SurfaceSelection,
  type UIEventHandler,
} from '@blocksuite/std';
import {
  GfxControllerIdentifier,
  type GfxViewportElement,
} from '@blocksuite/std/gfx';
import {
  CANVAS_WORLD_BOUNDS,
  CANVAS_WORLD_MAX,
  CANVAS_WORLD_MIN,
  initialCanvasWorldView,
} from '@blocksuite/store';
import { effect } from '@preact/signals-core';
import { css, html } from 'lit';
import { query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import debounce from 'lodash-es/debounce';

import {
  type CanvasChromePalette,
  CanvasWorldChrome,
} from './canvas-world-chrome.js';
import { EdgelessPageKeyboardManager } from './edgeless-keyboard.js';
import type { EdgelessRootService } from './edgeless-root-service.js';
import { observeEdgelessGridThemeAttributes } from './grid-theme-observer.js';
import { createEdgelessLineGridBackground } from './line-grid-background.js';
import { isCanvasElement } from './utils/query.js';

export class EdgelessRootBlockComponent extends BlockComponent<
  RootBlockModel,
  EdgelessRootService
> {
  static override styles = css`
    affine-edgeless-root {
      -webkit-user-select: none;
      user-select: none;
      display: block;
      height: 100%;
      touch-action: none;
    }

    .widgets-container {
      position: absolute;
      left: 0;
      top: 0;
      pointer-events: none;
      contain: size layout;
      height: 100%;
      width: 100%;
    }

    .widgets-container > * {
      pointer-events: auto;
    }

    .edgeless-background {
      position: absolute;
      inset: 0;
      pointer-events: none;
      background-color: var(--streamlain-background-primary-color);
      background-image:
        linear-gradient(transparent, transparent),
        var(
          --streamlain-edgeless-grid-background-image,
          radial-gradient(
            var(--streamlain-edgeless-grid-color) 1px,
            var(--streamlain-background-primary-color) 1px
          )
        );
    }

    .edgeless-container {
      height: 100%;
      color: var(--streamlain-text-primary-color);
      position: relative;
      background-color: var(
        --streamlain-edgeless-canvas-background-color,
        var(--streamlain-background-primary-color)
      );
    }

    .edgeless-world-chrome {
      position: absolute;
      inset: 0;
      pointer-events: none;
      overflow: hidden;
    }

    @media print {
      .edgeless-world-chrome {
        display: none;
      }
      .selected {
        background-color: transparent !important;
      }
    }
  `;

  private readonly _refreshLayerViewport = requestThrottledConnectedFrame(
    () => {
      if (
        document.documentElement.dataset
          .streamlainPerformanceSuspendViewportBackground === 'true'
      ) {
        return;
      }
      const { zoom, translateX, translateY } = this.gfx.viewport;

      if (this.backgroundElm) {
        const background = this.backgroundElm;
        const { width, height, left, top } = background.getBoundingClientRect();
        const view = {
          width,
          height,
          left,
          top,
          zoom,
          translateX,
          translateY,
          pixelRatio: window.devicePixelRatio,
        };
        this._worldChrome?.update(
          view,
          this._chromePalette,
          this.model.props.title.toString() || 'Canvas'
        );
        if (this._gridStyle === 'lines') {
          background.style.removeProperty('clip-path');
          const grid = createEdgelessLineGridBackground(
            {
              width,
              height,
              left,
              top,
              zoom,
              translateX,
              translateY,
              pixelRatio: window.devicePixelRatio,
            },
            this._lineGridPalette
          );
          background.style.setProperty(
            EDGELESS_GRID_BACKGROUND_IMAGE_VAR,
            grid?.image ?? 'none'
          );
          background.style.setProperty(
            'background-position',
            `0 0, ${grid?.position ?? '0 0'}`
          );
          background.style.setProperty(
            'background-size',
            `100% 100%, ${grid?.size ?? '100% 100%'}`
          );
          background.style.setProperty(
            'background-repeat',
            'no-repeat, no-repeat'
          );
          return;
        }

        // Layer 1: spotlight (screen-space), layer 2: world-anchored dot/cross tile.
        const screenGap = calculateGridScreenGap(zoom);
        const clipLeft = Math.max(0, translateX + CANVAS_WORLD_MIN * zoom);
        const clipTop = Math.max(0, translateY + CANVAS_WORLD_MIN * zoom);
        const clipRight = Math.max(
          0,
          width - translateX - CANVAS_WORLD_MAX * zoom
        );
        const clipBottom = Math.max(
          0,
          height - translateY - CANVAS_WORLD_MAX * zoom
        );
        background.style.setProperty(
          'clip-path',
          `inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px)`
        );
        background.style.setProperty('background-repeat', 'no-repeat, repeat');
        this.backgroundElm.style.setProperty(
          'background-position',
          `0 0, ${translateX}px ${translateY}px`
        );
        this.backgroundElm.style.setProperty(
          'background-size',
          `100% 100%, ${screenGap}px ${screenGap}px`
        );
      }
    },
    this
  );

  private _resizeObserver: ResizeObserver | null = null;

  private _worldChrome: CanvasWorldChrome | null = null;

  private _chromePalette: CanvasChromePalette = {
    line: '#777',
    surface: '#272a23',
    label: '#aaa',
  };

  private _initWorldChrome() {
    const host = this.querySelector<HTMLElement>('.edgeless-world-chrome');
    if (!host) return;
    this._worldChrome = new CanvasWorldChrome(host);
    let pointer: { x: number; y: number } | null = null;
    const refreshPointer = requestThrottledConnectedFrame(() => {
      if (!pointer || !this.backgroundElm) {
        this._worldChrome?.updatePointer(null);
        return;
      }
      const rect = this.backgroundElm.getBoundingClientRect();
      this._worldChrome?.updatePointer({
        x: pointer.x - rect.left,
        y: pointer.y - rect.top,
      });
    }, this);
    const move = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
      refreshPointer();
    };
    const leave = () => {
      pointer = null;
      refreshPointer();
    };
    this.addEventListener('pointermove', move);
    this.addEventListener('pointerleave', leave);
    let observedTitle = this.model.props.title.yText;
    observedTitle.observe(this._refreshLayerViewport);
    this._disposables.add(
      this.model.propsUpdated.subscribe(({ key }) => {
        if (key !== 'title') return;
        if (observedTitle !== this.model.props.title.yText) {
          observedTitle.unobserve(this._refreshLayerViewport);
          observedTitle = this.model.props.title.yText;
          observedTitle.observe(this._refreshLayerViewport);
        }
        this._refreshLayerViewport();
      })
    );
    this._disposables.add(() => {
      observedTitle.unobserve(this._refreshLayerViewport);
      this.removeEventListener('pointermove', move);
      this.removeEventListener('pointerleave', leave);
      this._worldChrome?.dispose();
      this._worldChrome = null;
      this.gfx.viewport.worldBounds = null;
    });
  }

  private _gridStyle: EdgelessGridStyle = 'lines';

  private _lineGridPalette: EdgelessLineGridPalette = {
    cell: '',
    block: '',
    sector: '',
  };

  private readonly _syncGridBackground = () => {
    if (!this.backgroundElm) return;

    const style = normalizeEdgelessGridStyle(
      document.documentElement.getAttribute(EDGELESS_GRID_STYLE_ATTRIBUTE)
    );
    const computedStyle = getComputedStyle(this.backgroundElm);
    const color =
      computedStyle.getPropertyValue(edgelessCanvasGridColorVar).trim() ||
      computedStyle.getPropertyValue('--streamlain-edgeless-grid-color').trim();
    this._gridStyle = style;
    const lineColor = (level: keyof EdgelessLineGridPalette) =>
      computedStyle
        .getPropertyValue(edgelessCanvasLineGridColorVars[level])
        .trim() || color;
    this._lineGridPalette = {
      cell: lineColor('cell'),
      block: lineColor('block'),
      sector: lineColor('sector'),
    };
    this._chromePalette = {
      line: this._lineGridPalette.sector || color || '#777',
      surface:
        computedStyle.getPropertyValue(edgelessCanvasBackgroundVar).trim() ||
        computedStyle.backgroundColor,
      label:
        computedStyle
          .getPropertyValue('--streamlain-text-secondary-color')
          .trim() || computedStyle.color,
    };
    if (style === 'lines') {
      this._refreshLayerViewport();
      return;
    }
    const backgroundImage = createEdgelessGridBackgroundImage(style, color);
    if (backgroundImage) {
      this.backgroundElm.style.setProperty(
        EDGELESS_GRID_BACKGROUND_IMAGE_VAR,
        backgroundImage
      );
    } else {
      this.backgroundElm.style.removeProperty(
        EDGELESS_GRID_BACKGROUND_IMAGE_VAR
      );
    }
    this._refreshLayerViewport();
  };

  keyboardManager: EdgelessPageKeyboardManager | null = null;

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get surfaceBlockModel() {
    return this.model.children.find(
      child => child.flavour === 'affine:surface'
    ) as SurfaceBlockModel;
  }

  get viewportElement(): HTMLElement {
    return this.std.get(ViewportElementProvider).viewportElement;
  }

  get fontLoader() {
    return this.std.get(FontLoaderService);
  }

  private _initFontLoader() {
    this.fontLoader.ready
      .then(() => {
        clearTextMeasurementCache();
        this.surface.refresh();
      })
      .catch(console.error);
  }

  private _initLayerUpdateEffect() {
    const updateLayers = requestThrottledConnectedFrame(() => {
      const blocks = Array.from(
        this.gfxViewportElm.children as HTMLCollectionOf<GfxBlockComponent>
      );

      blocks.forEach((block: GfxBlockComponent) => {
        block.updateZIndex?.();
      });
    });

    this._disposables.add(
      this.gfx.layer.slots.layerUpdated.subscribe(() => updateLayers())
    );
  }

  private _initPanEvent() {
    this.disposables.add(
      this.std.event.add('pan', ctx => {
        const { viewport } = this.gfx;
        if (viewport.locked) return;

        const multiPointersState = ctx.get('multiPointerState');
        const [p1, p2] = multiPointersState.pointers;

        const dx =
          (0.25 * (p1.delta.x + p2.delta.x)) /
          viewport.zoom /
          viewport.viewScale;
        const dy =
          (0.25 * (p1.delta.y + p2.delta.y)) /
          viewport.zoom /
          viewport.viewScale;

        // direction is opposite
        viewport.applyDeltaCenter(-dx, -dy);
      })
    );
  }

  private _initPinchEvent() {
    this.disposables.add(
      this.std.event.add('pinch', ctx => {
        const { viewport } = this.gfx;
        if (viewport.locked) return;

        const multiPointersState = ctx.get('multiPointerState');
        const [p1, p2] = multiPointersState.pointers;

        const currentCenter = new Point(
          0.5 * (p1.x + p2.x),
          0.5 * (p1.y + p2.y)
        );

        const lastDistance = Vec.dist(
          [p1.x - p1.delta.x, p1.y - p1.delta.y],
          [p2.x - p2.delta.x, p2.y - p2.delta.y]
        );
        const currentDistance = Vec.dist([p1.x, p1.y], [p2.x, p2.y]);

        const zoom = (currentDistance / lastDistance) * viewport.zoom;

        const [baseX, baseY] = viewport.toModelCoord(
          currentCenter.x,
          currentCenter.y
        );

        viewport.setZoom(zoom, new Point(baseX, baseY));

        return false;
      })
    );
  }

  private _initPixelRatioChangeEffect() {
    let media: MediaQueryList;

    const onPixelRatioChange = () => {
      if (media) {
        this.gfx.viewport.onResize();
        media.removeEventListener('change', onPixelRatioChange);
      }

      media = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      media.addEventListener('change', onPixelRatioChange);
      this._refreshLayerViewport();
    };

    onPixelRatioChange();

    this._disposables.add(() => {
      media?.removeEventListener('change', onPixelRatioChange);
    });
  }

  private _initRemoteCursor() {
    const setRemoteCursor = requestThrottledConnectedFrame(
      (pos: { x: number; y: number }) => {
        if (!this.gfx.viewport) return;
        const cursorPosition = this.gfx.viewport.toModelCoord(pos.x, pos.y);
        this.gfx.selection.setCursor({
          x: cursorPosition[0],
          y: cursorPosition[1],
        });
      },
      this
    );

    this.handleEvent('pointerMove', e => {
      const pointerEvent = e.get('pointerState');
      setRemoteCursor(pointerEvent);
    });
  }

  private _initResizeEffect() {
    const resizeObserver = new ResizeObserver((_: ResizeObserverEntry[]) => {
      this.gfx.selection.set(this.gfx.selection.surfaceSelections);
      this.gfx.viewport.onResize();
      this._refreshLayerViewport();
    });

    resizeObserver.observe(this.viewportElement);
    this._resizeObserver = resizeObserver;
  }

  private _initGridThemeEffect() {
    const viewport = this.closest('.affine-edgeless-viewport');
    if (!viewport) return;

    this.disposables.add(
      observeEdgelessGridThemeAttributes(viewport, this._syncGridBackground)
    );
  }

  private _initSlotEffects() {
    const { disposables } = this;

    this.disposables.add(
      this.std.get(ThemeProvider).theme$.subscribe(() => {
        this.surface.refresh();
        requestAnimationFrame(this._syncGridBackground);
      })
    );

    disposables.add(
      effect(() => {
        this.style.cursor = this.gfx.cursor$.value;
      })
    );
  }

  private _initViewport() {
    const { std, gfx } = this;

    const run = () => {
      // Camera bounds may include pre-existing outliers; never mutate their data.
      const content = gfx.elementsBound;
      const left = Math.min(CANVAS_WORLD_BOUNDS.x, content?.x ?? 0);
      const top = Math.min(CANVAS_WORLD_BOUNDS.y, content?.y ?? 0);
      const right = Math.max(
        CANVAS_WORLD_MAX,
        content ? content.x + content.w : 0
      );
      const bottom = Math.max(
        CANVAS_WORLD_MAX,
        content ? content.y + content.h : 0
      );
      gfx.viewport.worldBounds = {
        x: left,
        y: top,
        w: right - left,
        h: bottom - top,
      };
      const storedViewport = std.get(EditPropsStore).getStorage('viewport');
      if (!storedViewport && this.model.isEmpty()) {
        const { zoom, centerX, centerY } = initialCanvasWorldView(
          gfx.viewport.width,
          gfx.viewport.height
        );
        gfx.viewport.setViewport(zoom, [centerX, centerY]);
        return;
      }
      const animationFn = std.getOptional(
        NoteConfigExtension.identifier
      )?.pageBlockViewportFitAnimation;
      if (animationFn) {
        const pageBlock = this.model.children.find(
          (child): child is NoteBlockModel =>
            matchModels(child, [NoteBlockModel]) && child.isPageBlock()
        );
        if (pageBlock && animationFn({ std: this.std, note: pageBlock })) {
          return;
        }
      }

      if (storedViewport) {
        if ('xywh' in storedViewport) {
          const bound = Bound.deserialize(storedViewport.xywh);
          gfx.viewport.setViewportByBound(bound, storedViewport.padding);
        } else {
          const { zoom, centerX, centerY } = storedViewport;
          gfx.viewport.setViewport(zoom, [centerX, centerY]);
        }
        return;
      }

      this.gfx.fitToScreen();
    };

    run();

    // Save viewport position to localStorage
    const saveViewport = () => {
      if (this._isViewportPersistenceDisabled()) return;
      std.get(EditPropsStore).setStorage('viewport', {
        centerX: gfx.viewport.centerX,
        centerY: gfx.viewport.centerY,
        zoom: gfx.viewport.zoom,
      });
    };

    // Debounced save on viewport changes (survives browser close)
    const debouncedSave = debounce(saveViewport, 500);
    const subscription = gfx.viewport.viewportUpdated.subscribe(debouncedSave);

    this._disposables.add(() => {
      subscription.unsubscribe();
      debouncedSave.cancel();
      // Final save on disconnect (for graceful shutdown)
      saveViewport();
    });
  }

  private _isViewportPersistenceDisabled() {
    return !!this.closest('[data-streamlain-peek-preview="true"]');
  }

  private _initWheelEvent() {
    const wheelRouter = new EdgelessWheelGestureRouter();
    this._disposables.add(
      this.std.event.add('wheel', ctx => {
        const config = this.std.getOptional(EditorSettingProvider)?.setting$;
        const state = ctx.get('defaultState');
        const e = state.event as WheelEvent;
        const scrollZoom = config?.peek().edgelessScrollZoom ?? true;

        e.preventDefault();

        const { viewport } = this.gfx;
        if (viewport.locked) return;

        const action = wheelRouter.resolve(e, scrollZoom);

        if (action === 'zoom') {
          const rect = this.getBoundingClientRect();
          // Perform zooming relative to the mouse position
          const [baseX, baseY] = this.gfx.viewport.toModelCoord(
            e.clientX - rect.x,
            e.clientY - rect.y
          );

          const zoom = normalizeWheelDeltaY(e.deltaY, viewport.zoom, {
            deltaMode: e.deltaMode,
            ctrlKey: e.ctrlKey,
            viewportHeight: viewport.height,
          });
          viewport.setZoom(zoom, new Point(baseX, baseY), true);
          e.stopPropagation();
        }
        // pan
        else {
          const simulateHorizontalScroll = e.shiftKey && e.deltaX === 0;
          const dx = simulateHorizontalScroll
            ? e.deltaY / viewport.zoom
            : e.deltaX / viewport.zoom;
          const dy = simulateHorizontalScroll ? 0 : e.deltaY / viewport.zoom;

          viewport.applyDeltaCenter(dx, dy);
          viewport.viewportMoved.next([dx, dy]);
          e.stopPropagation();
        }
      })
    );
  }

  override bindHotKey(
    keymap: Record<string, UIEventHandler>,
    options?: { global?: boolean; flavour?: boolean }
  ): () => void {
    const { gfx } = this;
    const selection = gfx.selection;

    Object.keys(keymap).forEach(key => {
      if (key.length === 1 && key >= 'A' && key <= 'z') {
        const handler = keymap[key];

        keymap[key] = ctx => {
          const elements = selection.selectedElements;

          if (isSingleMindMapNode(elements) && !selection.editing) {
            const target = gfx.getElementById(
              elements[0].id
            ) as ShapeElementModel;
            if (target.text) {
              this.store.transact(() => {
                target.text!.delete(0, target.text!.length);
                target.text!.insert(0, key);
              });
            }
            mountShapeTextEditor(target, this);
          } else {
            handler(ctx);
          }
        };
      }
    });

    return super.bindHotKey(keymap, options);
  }

  override connectedCallback() {
    super.connectedCallback();

    this.keyboardManager = new EdgelessPageKeyboardManager(this);

    this.handleEvent('selectionChange', () => {
      const surface = this.host.selection.value.find(
        (sel): sel is SurfaceSelection => sel.is(SurfaceSelection)
      );
      if (!surface) return;

      const el = this.gfx.getElementById(surface.elements[0]);
      if (isCanvasElement(el)) {
        return true;
      }

      return;
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    this.keyboardManager = null;
  }

  override firstUpdated() {
    this._initWorldChrome();
    this._initSlotEffects();
    this._initGridThemeEffect();
    this._initResizeEffect();
    this._initPixelRatioChangeEffect();
    this._initFontLoader();
    this._initRemoteCursor();
    this._initLayerUpdateEffect();

    window.addEventListener(
      EDGELESS_GRID_STYLE_CHANGED_EVENT,
      this._syncGridBackground
    );
    this._disposables.add(() =>
      window.removeEventListener(
        EDGELESS_GRID_STYLE_CHANGED_EVENT,
        this._syncGridBackground
      )
    );

    this._initWheelEvent();
    this._initPanEvent();
    this._initPinchEvent();

    if (this.store.readonly) {
      this.gfx.tool.setTool(PanTool, { panning: true });
    } else {
      this.gfx.tool.setTool(DefaultTool);
    }

    this.gfx.viewport.elementReady.next(this.gfxViewportElm);
    this._initViewport();

    requestConnectedFrame(() => {
      this.requestUpdate();
    }, this);

    this._disposables.add(
      this.gfx.viewport.viewportUpdated.subscribe(() => {
        this._refreshLayerViewport();
      })
    );

    this._syncGridBackground();
    this._refreshLayerViewport();
  }

  override renderBlock() {
    const widgets = repeat(
      Object.entries(this.widgets),
      ([id]) => id,
      ([_, widget]) => widget
    );

    return html`
      <div class="edgeless-container">
        <div class="edgeless-background" aria-hidden="true"></div>
        <div
          class="edgeless-world-chrome"
          aria-hidden="true"
          data-html2canvas-ignore="true"
        ></div>
        <gfx-viewport
          .maxConcurrentRenders=${6}
          .viewport=${this.gfx.viewport}
          .getModelsInViewport=${() => {
            const blocks = this.gfx.grid.search(
              this.gfx.viewport.viewportBounds,
              {
                useSet: true,
                filter: ['block'],
              }
            );

            return blocks;
          }}
          .host=${this.host}
        >
          ${this.renderChildren(this.model)}
          ${this.renderChildren(this.surfaceBlockModel)}
        </gfx-viewport>
      </div>

      <!--
        Used to mount component before widgets
        Eg., canvas text editor
      -->
      <div class="edgeless-mount-point"></div>

      <div class="widgets-container">${widgets}</div>
    `;
  }

  @query('.edgeless-background')
  accessor backgroundElm: HTMLDivElement | null = null;

  @query('gfx-viewport')
  accessor gfxViewportElm!: GfxViewportElement;

  @query('.edgeless-mount-point')
  accessor mountElm: HTMLDivElement | null = null;

  @query('affine-surface')
  accessor surface!: SurfaceBlockComponent;
}
